import type {
  City,
  Country,
  State,
} from "react-country-state-city/dist/esm/types";

import { useEffect, useMemo, useState } from "react";
import { GetCity, GetCountries, GetState } from "react-country-state-city";

interface UseLocationDataParams {
  countryValue?: string;
  stateValue?: string;
}

interface UseLocationDataReturn {
  cities: City[];
  countries: Country[];
  handleCityChange: (cityId: number) => City | undefined;
  handleCountryChange: (countryId: number) => Country | undefined;
  handleStateChange: (stateId: number) => State | undefined;
  hasCities: boolean;
  hasStates: boolean;
  isCountrySelected: boolean;
  isStateSelected: boolean;
  states: State[];
}

export function useLocationData(
  params?: UseLocationDataParams,
): UseLocationDataReturn {
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  const selectedCountry = useMemo(
    () => countries.find((c) => c.name === params?.countryValue),
    [countries, params?.countryValue],
  );

  const selectedState = useMemo(
    () => states.find((s) => s.name === params?.stateValue),
    [states, params?.stateValue],
  );

  useEffect(() => {
    GetCountries().then(setCountries);
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      GetState(selectedCountry.id).then(setStates);
    } else {
      setStates([]);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedCountry && selectedState) {
      GetCity(selectedCountry.id, selectedState.id).then(setCities);
    } else {
      setCities([]);
    }
  }, [selectedCountry, selectedState]);

  const handleCountryChange = (id: number) =>
    countries.find((c) => c.id === id);

  const handleStateChange = (id: number) => states.find((s) => s.id === id);

  const handleCityChange = (id: number) => cities.find((c) => c.id === id);

  return {
    cities,
    countries,
    handleCityChange,
    handleCountryChange,
    handleStateChange,
    hasCities: selectedState?.hasCities ?? cities.length > 0,
    hasStates: selectedCountry?.hasStates ?? states.length > 0,
    isCountrySelected: !!selectedCountry,
    isStateSelected: !!selectedState,
    states,
  };
}
